import { QueryStringBuilderFactoryModule } from '@b2c/common/factories/QueryStringBuilder/QueryStringBuilder.factory';
import { ApiVersionFactoryModule } from '@b2c/common/factories/apiVersion/apiVersion.factory';
import { TODO } from '@b2c/types/index';
import angular, { IDocumentService, IHttpService, IQService, IRootScopeService, IWindowService } from 'angular';
import { StateParams } from 'angular-ui-router';
import _ from 'lodash';
import { BugsnagService, BugsnagServiceModule } from '../bugsnag';
import { ComboServiceModule } from '../combo/combo.service';
import { EventsServiceModule } from '../events/events.service';
import { FormsService, FormsServiceModule } from '../forms/forms.service';
import { MembershipService, MembershipServiceModule } from '../membership/membership.service';
import { LocalStorageService, LocalStorageServiceModule } from '../storage';
import { CartData, CartTemplateEvent } from './types';

export class CartService {
  static $inject = [
    '$rootScope',
    '$window',
    '$q',
    '$http',
    'ComboService',
    'FormsService',
    'ApiVersionFactory',
    'QueryStringBuilderFactory',
    'MembershipService',
    '$document',
    '$stateParams',
    'LocalStorageService',
    'BugsnagService',
  ];

  data = {} as CartData;

  cartPrev = 'cart-';

  cartId = '';

  isDocumentVisible = false;
  cartChangedFromAnotherTab = false;

  store = {
    combos: {} as TODO,
    forms: [] as TODO[],
  };

  CODES = {
    errors: {
      INVALID_NUMBER: 6100, // "The card number is not a valid credit card number"
      INVALID_EXPIRY_MONTH: 6101, // "The card's expiration month is invalid"
      INVALID_EXPIRY_YEAR: 6102, // "The card's expiration year is invalid"
      INVALID_CVC: 6103, // "The card's security code is invalid"
      INCORRECT_NUMBER: 6104, // "The card number is incorrect"
      EXPIRED_CARD: 6105, // "The card has expired"
      INCORRECT_CVC: 6106, // "The card's security code is incorrect"
      INCORRECT_ZIP: 6107, // "The card's zip code failed validation"
      INCORRECT_ADDRESS: 6113, // "The card's address failed validation"
      CARD_DECLINED: 6108, // "The card was declined"
      MISSING: 6109, // "There is no card on a customer that is being charged"
      PROCESSING_ERROR: 6110, // "An error occurred while processing the card"
      STREET_ADDRESS1_REQUIRED: 5082, // "The card's street address is missing"
      CITY_REQUIRED: 5083, // "The card's street city is missing"
      STATE_REQUIRED: 5084, // "The card's state is missing"
      ZIP_REQUIRED: 5085, // "The card's zip code is missing"
      COUNTRY_REQUIRED: 5086, // "The card's country is missing"
      TICKET_LIMIT_EXCEEDED: 9049, // "Ticket limit for at least one ticket type has been exceeded"
    },

    find: (code: string) => {
      let type;
      let result: TODO | false = false;
      const _code = parseInt(code, 10);

      if (!isNaN(_code)) {
        for (type in this.CODES.errors) {
          if (this.CODES.errors.hasOwnProperty(type) && this.CODES.errors[type] === _code) {
            result = {
              type: type,
              code: this.CODES.errors[type],
            };
            break;
          }
        }
      }

      return result;
    },
  };

  api = {
    av_cart: new this.ApiVersionFactory({
      url: '/v1/b2c/shopping_cart',
      version: 'v2',
    }),

    av_checkout: new this.ApiVersionFactory({
      url: '/v1/b2c/checkout',
      version: 'v3',
    }),

    av_uuidObjectRegistry: new this.ApiVersionFactory({
      url: '/v1/b2c/uuidobjectregistry',
      version: 'v2',
    }),

    /**
     * Gets and creates a cart if it doesn't already exist
     */
    detail: (props) => {
      const qs = new this.QueryStringBuilderFactory({
        props: props,
        fields: {
          optional: ['membershipId', 'membershipIds'],
        },
      });

      return this.$http<TODO>({
        method: 'get',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_cart.url + qs.query,
      });
    },

    update: (props) => {
      if (props.membershipId) {
        props.data.membershipId = props.membershipId;
      }

      if (props.membershipIds) {
        props.data.membershipIds = props.membershipIds;
      }

      props.data.couponCode = props.couponCode || null;
      props.data.couponCodes = props.couponCodes || [];

      props.data.donationDetail = props.donationDetail || {};

      props.data.verifyEntitlements = true;

      if (props.updateType) {
        props.data.updateType = props.updateType;
      }

      return this.$http<TODO>({
        method: 'put',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_cart.url,
        data: props.data,
      });
    },

    remove: (props) => {
      return this.$http<TODO>({
        method: 'delete',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
        url: this.api.av_cart.url + props.itemId,
      });
    },

    clear: () => {
      return $.removeCookie('cart', { path: '/' });
    },

    // TODO: is shoppingCartId really optional?
    deleteShoppingCart: (tenantId: string, shoppingCartId?: string) => {
      return this.$http<TODO>({
        method: 'delete',
        headers: {
          'x-b2c-tenant-id': tenantId,
          'x-b2c-cart-id': shoppingCartId,
        },
        url: this.api.av_cart.url,
      });
    },

    checkout: (props: { tenantId: string; requestUUID: string; recaptchaToken: string | null; data: TODO }) => {
      return this.$http<TODO>({
        method: 'post',
        headers: {
          'x-b2c-tenant-id': props.tenantId,
          'x-acme-request-uuid': props.requestUUID,
          'x-acme-captcha-token': props.recaptchaToken,
        },
        data: props.data,
        url: this.api.av_checkout.url,
      });
    },

    uuidObjectRegistry: (props) => {
      return this.$http<TODO>({
        method: 'get',
        url: this.api.av_uuidObjectRegistry.url + '/' + props.uuid,
        headers: {
          'x-b2c-tenant-id': props.tenantId,
        },
      });
    },
  };

  constructor(
    private $rootScope: IRootScopeService,
    private $window: IWindowService,
    private $q: IQService,
    private $http: IHttpService,
    private ComboService: TODO,
    private formsService: FormsService,
    private ApiVersionFactory: TODO,
    private QueryStringBuilderFactory: TODO,
    private membershipService: MembershipService,
    private $document: IDocumentService,
    private $stateParams: StateParams,
    private localStorageService: LocalStorageService,
    private bugsnagService: BugsnagService
  ) {
    this.init();
  }

  /**
   * Omits `expirationDate` and performs deep equality check on 2 cart objects
   */
  checkCartEquality(oldValue: Record<string, any>, newValue: Record<string, any>) {
    function normalizeCartData(cartData) {
      return _.omit(cartData, ['expirationDate']);
    }

    oldValue = normalizeCartData(oldValue);
    newValue = normalizeCartData(newValue);

    return _.isEqual(oldValue, newValue);
  }

  /**
   * We listen to storage events because we need to know when users have changed
   * their carts from other tabs.
   */
  attachStorageListener() {
    return onEvent(this.$window as any, 'storage', (event: StorageEvent) => {
      if (event.key === this.cartId) {
        const oldValue = JSON.parse(event.oldValue!);
        const newValue = JSON.parse(event.newValue!);

        const isEqual = this.checkCartEquality(oldValue, newValue);

        if (!isEqual) {
          this.cartChangedFromAnotherTab = true;
        }
      }

      if (event.key === 'membership') {
        this.$rootScope.$emit('Membership::reinitializedAfterChangeFromAnotherTab');
      }
    });
  }

  /**
   * Wait for the document to be visible again to avoid unnecessary work.
   */
  attachDocumentVisibilityListener() {
    const doc = this.$document[0] as Document;
    this.isDocumentVisible = doc.visibilityState === 'visible';

    return onEvent(
      doc as any,
      'visibilitychange',
      _.debounce(() => {
        this.isDocumentVisible = doc.visibilityState === 'visible';

        if (this.cartChangedFromAnotherTab && this.isDocumentVisible) {
          const params = this.getCartInitParams();
          this.initCart(params, true).then((response) => {
            this.cartChangedFromAnotherTab = false;

            /**
             * We emit this event on the rootScope so any component that's interested in this can recalculate their
             * state from the new cart data response.
             *
             * @note This should be a lot easier if we adopt the Observer pattern in our services
             */
            this.$rootScope.$emit('Cart::reinitializedAfterChangeFromAnotherTab', { response: response });
          });
        }
      }, 500)
    );
  }

  init() {
    const clearStorageListener = this.attachStorageListener();
    const clearDocumentVisibilityListener = this.attachDocumentVisibilityListener();

    this.$rootScope.$on('$destroy', function () {
      clearStorageListener();
      clearDocumentVisibilityListener();
    });

    return true;
  }

  getCartInitParams() {
    const isExpiredMembership = this.membershipService.checkExpiredVerifiedMembership(this.$stateParams.tenantId);
    const membership = !isExpiredMembership && this.membershipService.getVerifiedMember();

    return {
      tenantId: this.$stateParams.tenantId,
      membershipId: isExpiredMembership ? null : membership.membershipId,
      membershipIds: isExpiredMembership ? null : this.membershipService.getMembershipIds(true),
    };
  }

  /**
   * Returns shopping cart details payload
   */
  getShoppingCart() {
    const params = this.getCartInitParams();

    return this.api.detail(params).then((response) => response.data.payload);
  }

  initCart(props: TODO, force?: boolean) {
    const deferred = this.$q.defer<TODO>();
    const cartId = this.cartPrev + props.tenantId;
    let cartData = false;
    const forceApiCall = !!force;

    this.cartId = cartId;

    cartData = this.getCartSession();

    if (cartData && !forceApiCall) {
      deferred.resolve({
        data: {
          payload: cartData,
          success: true,
        },
      });
    } else {
      this.api
        .detail(props)
        .then((res) => {
          this.setCartSession(res.data.payload);
          deferred.resolve(res);
        })
        .catch((res) => {
          deferred.reject(res);
        });
    }

    return deferred.promise;
  }

  getTenantId() {
    return this.cartId.substring(this.cartPrev.length);
  }

  getCartSession() {
    const cartSession = this.localStorageService.getItem<TODO>(this.cartId);

    if (cartSession && this.checkCartExpirationDate()) {
      this.data = cartSession;
      return cartSession;
    }

    return null;
  }

  setCartSession(data) {
    this.data = data;
    this.data.expirationDate = this.getCartExpirationDate();
    this.localStorageService.setItem(this.cartId, this.data);
  }

  refreshCartSession() {
    this.localStorageService.setItem(this.cartId, this.data);
  }

  killCartSession() {
    return this.localStorageService.removeItem(this.cartId);
  }

  clear() {
    this.killCartSession();
    return this.api.clear();
  }

  getCartExpirationDate() {
    const expDate = new Date();
    expDate.setDate(expDate.getDate() + 1);
    return expDate;
  }

  checkCartExpirationDate() {
    const now = new Date();
    const cartSession = this.localStorageService.getItem<TODO>(this.cartId);

    if (!cartSession) {
      return false;
    }

    const expirationDate = cartSession.expirationDate ? new Date(cartSession.expirationDate) : false;
    return expirationDate && expirationDate > now;
  }

  updateDonation(orders) {
    if (orders.length) {
      this.data.donations = [];
    }

    for (let i = 0, ii = orders.length; i < ii; i++) {
      if (this.isDonationType(orders[i])) {
        this.data.donations?.push(angular.extend({ inventoryId: orders[i].id }, orders[i]));
        this.data.donationDetail = orders[i].donationDetail;
      }
    }

    this.refreshCartSession();
  }

  update(orders, combo) {
    let entry;
    let event;
    let addOns;
    let eventId;
    let tickets;
    let comboEvents;

    // Save old combos before destroying cart
    this.storeCombos();
    this.storeForms();
    this.clearItems();
    for (let i = 0, ii = orders.length; i < ii; i++) {
      if (!orders[i].id) {
        continue;
      }
      tickets = [];
      addOns = [];

      if (this.isMembershipType(orders[i].itemType)) {
        if (orders[i].isInvalidMembership) {
          orders[i].quantity = 0;
        } else {
          orders[i].quantity = 1;
        }
        this.data.memberships.push(orders[i]);
      } else if (this.isDonationType(orders[i])) {
        this.data.donations.push(angular.extend({ inventoryId: orders[i].id }, orders[i]));
        this.data.donationDetail = orders[i].donationDetail;
      } else if (orders[i].itemType === 'Combo') {
        if (orders[i].type === 'fixedPrice') {
          // Combos
          for (let j = 0, jj = orders[i].priceList.prices.length; j < jj; j++) {
            tickets.push({
              name: orders[i].priceList.prices[j].personType.name,
              price: orders[i].priceList.prices[j].price,
              ticketTypeId: orders[i].priceList.prices[j].personType.id,
              description: orders[i].priceList.prices[j].personType.description,
              quantity: orders[i].priceList.prices[j].quantity,
            });
          }
        } else if (orders[i].type === 'discountCode') {
          for (let j = 0, jj = orders[i].templates.entries.length; j < jj; j++) {
            entry = orders[i].templates.entries[j];
            if (entry.template) {
              for (let k = 0, kk = entry.template.priceLists[0].prices.length; k < kk; k++) {
                if (
                  entry.template.priceLists[0].prices[k].quantity &&
                  entry.template.priceLists[0].prices[k].quantity >= 0
                ) {
                  tickets.push({
                    eventTemplateId: entry.template.id,
                    ticketTypeId: entry.template.priceLists[0].prices[k].personType.id,
                    name: entry.template.priceLists[0].prices[k].personType.name,
                    price: entry.template.priceLists[0].prices[k].price,
                    description: entry.template.priceLists[0].prices[k].personType.description,
                    quantity: entry.template.priceLists[0].prices[k].quantity,
                  });
                }
              }
            } else if (entry.summaries) {
              for (let k = 0, kk = entry.summaries.length; k < kk; k++) {
                for (let l = 0, ll = entry.summaries[k].priceLists[0].prices.length; l < ll; l++) {
                  if (
                    entry.summaries[k].priceLists[0].prices[l].quantity &&
                    entry.summaries[k].priceLists[0].prices[l].quantity >= 0
                  ) {
                    tickets.push({
                      eventTemplateId: entry.summaries[k].id,
                      ticketTypeId: entry.summaries[k].priceLists[0].prices[l].personType.id,
                      name: entry.summaries[k].priceLists[0].prices[l].personType.name,
                      price: entry.summaries[k].priceLists[0].prices[l].price,
                      description: entry.summaries[k].priceLists[0].prices[l].personType.description,
                      quantity: entry.summaries[k].priceLists[0].prices[l].quantity,
                    });
                  }
                }
              }
            }
          }
        }

        if (orders[i].hasOwnProperty('addOns') && orders[i].addOns instanceof Array) {
          // Combos
          for (let j = 0, jj = orders[i].addOns.length; j < jj; j++) {
            if (orders[i].addOns[j].addOn && typeof orders[i].addOns[j].addOn === 'object') {
              addOns.push({
                id: orders[i].addOns[j].addOn.id,
                inventoryId: orders[i].addOns[j].addOn.id,
                name: orders[i].addOns[j].addOn.name,
                price: orders[i].addOns[j].price,
                description: orders[i].addOns[j].addOn.description,
                quantity: orders[i].addOns[j].totalQuantity,
              });
            }
          }
        }

        if (orders[i].hasOwnProperty('templates') && orders[i].templates.entries instanceof Array) {
          if (orders[i].templates.entries instanceof Array) {
            for (let m = 0, mm = orders[i].templates.entries.length; m < mm; m++) {
              if (orders[i].templates.entries[m].template) {
                this.data.forms = this.data.forms.concat(
                  this.getFormsForOrder(
                    angular.extend({}, { itemType: 'Event' }, orders[i].templates.entries[m].template)
                  )
                );
              }
            }
          }

          this.data.comboItems.push({
            comboTemplateId: orders[i].id,
            events: [],
            tickets: tickets,
            addOns: addOns,
          });

          if (combo.comboTemplateId === orders[i].id && combo.eventTemplateIds instanceof Array) {
            // If a change has occurred outside of the template update with new data
            for (let j = 0, jj = combo.eventTemplateIds.length; j < jj; j++) {
              event = this.ComboService.findEventTemplate(orders[i], combo.eventTemplateIds[j]);
              if (event.selectedHourId) {
                eventId = event.selectedHourId;
              } else {
                eventId = '';
              }

              this.data.comboItems[this.data.comboItems.length - 1].events.push({
                eventTemplateId: event.id,
                eventId: eventId,
              });
            }
          } else if (this.hasStore('combos')) {
            // Use internal store to restore existing combo
            comboEvents = this.fetchCombo(orders[i].id).events;

            if (comboEvents) {
              for (let j = 0, jj = comboEvents.length; j < jj; j++) {
                if (comboEvents[j].eventTemplateId) {
                  event = this.ComboService.findEventTemplate(orders[i], comboEvents[j].eventTemplateId);
                } else {
                  event = this.ComboService.findEventTemplateByName(orders[i], comboEvents[j].eventName);
                }
                if (event.selectedHourId) {
                  eventId = event.selectedHourId;
                } else {
                  eventId = '';
                }

                this.data.comboItems[this.data.comboItems.length - 1].events.push({
                  eventTemplateId: event.id,
                  eventId: eventId,
                });
              }
            }
          }
        }
      } else {
        this.data.forms = this.data.forms.concat(this.getFormsForOrder(orders[i]));

        if (
          orders[i].hasOwnProperty('priceLists') &&
          orders[i].priceLists instanceof Array &&
          orders[i].priceLists[0] &&
          orders[i].priceLists[0].hasOwnProperty('prices') &&
          orders[i].priceLists[0].prices instanceof Array
        ) {
          for (let j = 0, jj = orders[i].priceLists[0].prices.length; j < jj; j++) {
            tickets.push({
              name: orders[i].priceLists[0].prices[j].personType.name,
              price: orders[i].priceLists[0].prices[j].price,
              ticketTypeId: orders[i].priceLists[0].prices[j].personType.id,
              description: orders[i].priceLists[0].prices[j].personType.description,
              quantity: orders[i].priceLists[0].prices[j].quantity,
            });
            if (
              orders[i].priceLists[0].prices[j].retailQuantity !== null &&
              orders[i].priceLists[0].prices[j].retailQuantity !== undefined
            ) {
              tickets.push({
                name: orders[i].priceLists[0].prices[j].personType.name,
                price: orders[i].priceLists[0].prices[j].discountedPrice,
                ticketTypeId: orders[i].priceLists[0].prices[j].personType.id,
                description: orders[i].priceLists[0].prices[j].personType.description,
                quantity: orders[i].priceLists[0].prices[j].retailQuantity || 0,
                isRetail: true,
              });
            }
          }
        }

        if (orders[i].hasOwnProperty('addOns') && orders[i].addOns.inventoryAddOns instanceof Array) {
          for (let j = 0, jj = orders[i].addOns.inventoryAddOns.length; j < jj; j++) {
            addOns.push({
              id: orders[i].addOns.inventoryAddOns[j].id,
              inventoryId: orders[i].addOns.inventoryAddOns[j].id,
              name: orders[i].addOns.inventoryAddOns[j].name,
              price: orders[i].addOns.inventoryAddOns[j].price,
              description: orders[i].addOns.inventoryAddOns[j].description,
              quantity: orders[i].addOns.inventoryAddOns[j].totalQuantity,
            });
          }
        }

        this.data.templates?.push({
          eventTemplateId: orders[i].id,
          events: [
            {
              eventId: orders[i].selectedHourId,
              tickets: tickets,
              addOns: addOns,
            } as TODO,
          ],
        });
      }
    }

    this.cleanForms();

    // We have used our combo store clean up to avoid dupes
    this.clearStore('combos');
    this.clearStore('forms');
    this.refreshCartSession();
  }

  removeDuplicates(orders) {
    const ordersFilter: TODO = {};
    const ordersFiltered: TODO[] = [];

    for (let i = 0, ii = orders.length; i < ii; i++) {
      if (!orders[i].id) {
        continue;
      }
      ordersFilter[orders[i].id + '-' + orders[i].itemType] = orders[i];
    }

    for (const item in ordersFilter) {
      ordersFiltered.push(ordersFilter[item]);
    }

    return ordersFiltered;
  }

  cleanForms() {
    const eventIds: TODO[] = [];
    const forms: TODO[] = [];
    let hasTickets = false;

    // getting events ids from combos
    if (this.data.comboItems instanceof Array && this.data.comboItems.length > 0) {
      for (let i = 0, ilen = this.data.comboItems.length; i < ilen; i++) {
        hasTickets = false;
        for (let j = 0, jlen = this.data.comboItems[i].tickets.length; j < jlen; j++) {
          if (this.data.comboItems[i].tickets[j].quantity > 0) {
            hasTickets = true;
          }
        }
        for (let k = 0, klen = this.data.comboItems[i].events.length; k < klen; k++) {
          if (hasTickets) {
            eventIds.push(this.data.comboItems[i].events[k].eventId);
          }
        }
      }
    }

    // getting events Ids from templates
    if (this.data.templates instanceof Array && this.data.templates.length > 0) {
      for (let i = 0, ilen = this.data.templates.length; i < ilen; i++) {
        for (let j = 0, jlen = this.data.templates[i].events.length; j < jlen; j++) {
          const eventTickets = this.data.templates?.[i].events?.[j].tickets ?? [];
          for (let k = 0, klen = eventTickets.length; k < klen; k++) {
            if (eventTickets[k].quantity > 0) {
              eventIds.push(this.data.templates[i].events[j].eventId);
            }
          }
        }
      }
    }

    // check if event with form exist
    if (this.data.forms instanceof Array) {
      for (let i = 0, ilen = this.data.forms.length; i < ilen; i++) {
        if (eventIds.indexOf(this.data.forms[i].itemId) > -1) {
          forms.push(this.data.forms[i]);
        }
      }
    }

    this.formsService.setForms(forms);
    this.data.forms = forms;

    this.refreshCartSession();
  }

  getFormsForOrder(order) {
    const hasForms = this.hasStore('forms');
    let formEntry;
    let form;
    const result: TODO[] = [];

    if (order.orderForms instanceof Array) {
      for (let j = 0, jj = order.orderForms.length; j < jj; j++) {
        if (
          order.orderForms[j].preferences &&
          order.orderForms[j].preferences.b2c &&
          order.orderForms[j].preferences.b2c.toLowerCase() !== 'none'
        ) {
          formEntry = angular.extend(
            {
              itemId: order.selectedHourId,
              itemName: order.name,
              itemType: order.itemType,
            },
            order.orderForms[j]
          );

          if (hasForms) {
            const found = this.findStoredForm({
              formId: order.orderForms[j].id,
              itemId: order.selectedHourId,
              itemName: order.name,
              itemType: order.itemType,
              pricePointId: null, // Add when ticket type forms are ready
              pricePointName: null, // Add when ticket type forms are ready
              oncePerCart: order.oncePerCart,
            });
            if (found) {
              formEntry.formResponseId = found.form.formResponseId;
            }
          }

          form = this.formsService.addForm(formEntry);
          result.push({
            formId: form.id,
            formResponseId: form.formResponseId,
            itemId: form.itemId,
            itemName: form.itemName,
            itemType: form.itemType,
            oncePerCart: form.oncePerCart,
          });
        }
      }
    }
    return result;
  }

  isEvent(item) {
    return item.itemType === 'Event';
  }

  isCombo(item) {
    return item.itemType === 'Combo';
  }

  isMembershipType(type) {
    const purchaseTypes = this.membershipService.purchaseTypesArray;
    return type && purchaseTypes.indexOf(type) > -1;
  }

  isDonationType(order) {
    const type = order.itemType;
    const donationType = order.hasOwnProperty('type') && (order.type === 'donation' || order.type === 'fixedDonation');
    return type && type === 'Inventory' && donationType;
  }

  checkCapacity(eventTime, comboTemplateId) {
    let total;
    let r = false;
    const event = eventTime.event;
    let found;

    if (comboTemplateId) {
      found = this.findComboItemEvent(comboTemplateId, event.templateId);
      if (found !== false) {
        total = 0;
        for (let j = 0, jj = this.data.comboItems[found.comboIndex].tickets.length; j < jj; j++) {
          total += this.data.comboItems[found.comboIndex].tickets[j].quantity;
        }
        if (eventTime.availableSeats > 0 && eventTime.availableSeats >= total) {
          r = true;
        }
      }
    } else {
      found = this.findEventItem(event.templateId);
      if (found !== false) {
        for (let i = 0, ii = this.data.templates[found].events.length; i < ii; i++) {
          total = 0;
          for (let j = 0, jj = this.data.templates[found].events[i].tickets.length; j < jj; j++) {
            total += this.data.templates[found].events[i].tickets[j].quantity;
          }
          if (eventTime.availableSeats > 0 && eventTime.availableSeats >= total) {
            r = true;
            break;
          }
        }
      }
    }

    return r;
  }

  getItemCount() {
    let r = 0;

    if (this.hasEventItems()) {
      r += this.data.templates.length;
    }

    if (this.hasComboItems()) {
      r += this.data.comboItems.length;
    }

    if (this.hasMembershipItems()) {
      r += this.data.memberships.length;
    }

    if (this.hasDonationItems()) {
      r += this.data.donations.length;
    }

    return r;
  }

  getEventCount() {
    let count = 0;

    count += this.data.templates.length;

    if (this.data.comboItems.length > 0) {
      for (let i = 0, ii = this.data.comboItems.length; i < ii; i++) {
        if (this.data.comboItems[i].events instanceof Array) {
          count += this.data.comboItems[i].events.length;
        }
      }
    }

    return count;
  }

  getEvents() {
    const events: CartTemplateEvent[] = [];
    const templates = this.data.templates ?? [];

    if (this.hasEventItems()) {
      for (let i = 0, ii = templates.length; i < ii; i++) {
        if (Array.isArray(templates[i].events)) {
          events.push(...templates[i].events);
        }
      }
    }

    return events;
  }

  getEventsWithTemplateIds() {
    let events;
    let r = [];

    if (this.hasEventItems()) {
      for (let i = 0, ii = this.data.templates.length; i < ii; i++) {
        if (this.data.templates[i].events instanceof Array) {
          events = [];
          for (let j = 0, jj = this.data.templates[i].events.length; j < jj; j++) {
            const event = $.extend({}, this.data.templates[i].events[j]);
            event.templateId = this.data.templates[i].eventTemplateId;
            events.push(event);
          }
          r = r.concat(events);
        }
      }
    }

    return r;
  }

  getComboEvents() {
    let r = [];

    if (this.hasComboItems()) {
      for (let i = 0, ii = this.data.comboItems.length; i < ii; i++) {
        if (this.data.comboItems[i].events instanceof Array) {
          r = r.concat(this.data.comboItems[i].events);
        }
      }
    }

    return r;
  }

  getComboEventsWithTemplateIds() {
    let result: TODO[] = [];

    if (this.hasComboItems()) {
      for (let i = 0, ii = this.data.comboItems.length; i < ii; i++) {
        if (this.data.comboItems[i].events instanceof Array) {
          const events: TODO[] = [];
          for (let j = 0, jj = this.data.comboItems[i].events.length; j < jj; j++) {
            const event = $.extend({}, this.data.comboItems[i].events[j]);
            event.comboTemplateId = this.data.comboItems[i].comboTemplateId;
            event.comboTemplateName = this.data.comboItems[i].comboTemplateName;
            events.push(event);
          }
          result = result.concat(events);
        }
      }
    }

    return result;
  }

  getEventTemplates() {
    if (this.hasEventItems()) {
      return this.data.templates ?? [];
    }

    return [];
  }

  getCombos() {
    if (this.hasComboItems()) {
      return this.data.comboItems ?? [];
    }

    return [];
  }

  getComboChangedDates(ids, date) {
    let comboIds;
    let updateIds;
    let result: TODO | false = false;

    if (typeof ids === 'string') {
      updateIds = ids.split(',');

      if (updateIds instanceof Array) {
        for (let i = 0, ii = updateIds.length; i < ii; i++) {
          if (updateIds[i].indexOf('|') > -1) {
            // 0: Combo Id
            // 1: Event Template Id
            comboIds = updateIds[i].split('|');
            if (!result) {
              result = {};
              if (!result.hasOwnProperty(comboIds[0])) {
                result[comboIds[0]] = {};
              }
            }

            result[comboIds[0]][comboIds[1]] = date;
          }
        }
      }
    }

    return result;
  }

  getEventChangedDates(ids, date) {
    let result: TODO | false = false;
    let updateIds;

    if (typeof ids === 'string') {
      updateIds = ids.split(',');
      for (let i = 0, ii = updateIds.length; i < ii; i++) {
        if (!result) {
          result = {};
        }
        result[updateIds[i]] = date;
      }
    }

    return result;
  }

  getChangedDates(type, ids, date) {
    let r = false;

    switch (type) {
      case 'combo':
        r = this.getComboChangedDates(ids, date);
        break;
      case 'event':
        r = this.getEventChangedDates(ids, date);
        break;
    }

    return r;
  }

  getCartDate(event, useToday) {
    const d = new Date();
    let result: TODO = useToday ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0) : false;
    const index = this.findItem(event.id);
    const template = index !== undefined && index !== false && this.data.templates[index];

    if (
      template &&
      event.hasOwnProperty('priceLists') &&
      event.priceLists instanceof Array &&
      event.priceLists[0] &&
      event.priceLists[0].hasOwnProperty('prices') &&
      event.priceLists[0].prices instanceof Array
    ) {
      if (template.events[0].startTime) {
        result = template.events[0].startTime.split('T')[0] + 'T00:00:00';
      }
    }

    return result;
  }

  restoreValues(cartItem, changedEventDateIds) {
    if (this.isCombo(cartItem)) {
      // This is a combo
      cartItem = this.restoreComboValues(
        cartItem,
        this.getChangedDates('combo', changedEventDateIds.eventIds, changedEventDateIds.date)
      );
    } else if (this.isMembershipType(cartItem.itemType)) {
      // This is a membership
      cartItem = this.restoreMembershipValues(cartItem);
    } else if (this.isDonationType(cartItem)) {
      // This is a donation
      cartItem = this.restoreDonationValues(cartItem);
    } else {
      // This is an event
      cartItem = this.restoreEventValues(
        cartItem,
        this.getChangedDates('event', changedEventDateIds.eventIds, changedEventDateIds.date)
      );
    }

    return cartItem;
  }

  restoreMembershipValues(membership) {
    let index;

    if (!membership.itemId) {
      index = this.findMembershipItem(
        membership.membershipInfo.membershipCategoryId,
        null,
        membership.membershipInfo.isGift
      );
    } else {
      index = this.findMembershipItemById(membership.itemId);
    }

    const currentMembership = this.data.memberships[index];

    if (currentMembership && membership.hasOwnProperty('membershipInfo')) {
      if (membership.hasOwnProperty('offerings') && membership.offerings instanceof Array) {
        for (let i = 0, ii = membership.offerings.length; i < ii; i++) {
          if (membership.offerings[i].id === currentMembership.membershipInfo.membershipOfferingId) {
            if (
              membership.offerings[i].hasOwnProperty('priceLists') &&
              membership.offerings[i].priceLists instanceof Array &&
              membership.offerings[i].priceLists[0].hasOwnProperty('prices') &&
              membership.offerings[i].priceLists[0].prices instanceof Array
            ) {
              for (let j = 0, jj = membership.offerings[i].priceLists[0].prices.length; j < jj; j++) {
                if (
                  membership.offerings[i].priceLists[0].prices[j].personType.id ===
                  currentMembership.membershipInfo.pricePointId
                ) {
                  if (!membership.membershipInfo.membershipOfferingId) {
                    membership.membershipInfo.membershipOfferingId =
                      currentMembership.membershipInfo.membershipOfferingId;
                  }
                  if (!membership.membershipInfo.pricePointId) {
                    membership.membershipInfo.pricePointId = currentMembership.membershipInfo.pricePointId;
                  }
                  membership.quantity = currentMembership.quantity;
                }
              }
            }
          }
        }
      }
    }

    return membership;
  }

  restoreDonationValues(donation) {
    const index = this.findItem(donation.id, donation.itemType, donation.type);
    const currentDonation = this.data.donations[index];

    if (currentDonation && donation.hasOwnProperty('type')) {
      donation.type = currentDonation.type;
      donation.donationDetail = currentDonation.donationDetail || {};

      if (!!parseFloat(currentDonation.price) || !!parseFloat(currentDonation.amount)) {
        donation.price = currentDonation.price || currentDonation.amount;
      }
    }

    return donation;
  }

  restoreEventValues(event, dates) {
    let selectedStartDate;
    const index = this.findItem(event.id);
    const template = this.data.templates[index];
    let found = false;

    if (
      template &&
      event.hasOwnProperty('priceLists') &&
      event.priceLists instanceof Array &&
      event.priceLists[0] &&
      event.priceLists[0].hasOwnProperty('prices') &&
      event.priceLists[0].prices instanceof Array
    ) {
      if (!event.selectedDate && template.events[0].startTime) {
        selectedStartDate = template.events[0].startTime.split('T');
        event.selectedDate = selectedStartDate[0] + 'T00:00:00';
      }

      event.selectedHourId = template.events[0].eventId;
      event.cartedPrices = [];

      for (let i = 0, ii = template.events[0].tickets.length; i < ii; i++) {
        found = false;
        for (let j = 0, jj = event.priceLists[0].prices.length; j < jj; j++) {
          if (event.priceLists[0].prices[j].personType.id === template.events[0].tickets[i].ticketTypeId) {
            if (template.events[0].tickets[i].isRetail) {
              event.priceLists[0].prices[j].retailQuantity = template.events[0].tickets[i].quantity;
            } else {
              event.priceLists[0].prices[j].retailQuantity = 0;
              event.priceLists[0].prices[j].quantity = template.events[0].tickets[i].quantity;
            }
            found = true;
            break;
          }
        }
        //The event's price list is different from the event template's price list,
        // but we need these quantities, so save them for later.
        if (!found) {
          event.cartedPrices.push(
            angular.extend({}, template.events[0].tickets[i], {
              retailQuantity: template.events[0].tickets[i].isRetail ? template.events[0].tickets[i].quantity : 0,
              personType: { id: template.events[0].tickets[i].ticketTypeId },
            })
          );
        }
      }

      if (
        template.events[0].hasOwnProperty('addOns') &&
        event.hasOwnProperty('addOns') &&
        event.addOns.inventoryAddOns instanceof Array
      ) {
        for (let i = 0, ii = template.events[0].addOns.length; i < ii; i++) {
          for (let j = 0, jj = event.addOns.inventoryAddOns.length; j < jj; j++) {
            if (event.addOns.inventoryAddOns[j].id === template.events[0].addOns[i].inventoryId) {
              event.addOns.inventoryAddOns[j].totalQuantity = template.events[0].addOns[i].quantity;
              break;
            }
          }
        }
      }
    }

    return event;
  }

  restoreComboValues(combo, dates) {
    let comboEvent;
    let selectedStartDate;
    const selectedTemplateIds: TODO[] = [];
    const index = this.findItem(combo.id, 'combo');
    const template = this.data.comboItems[index];

    if (template) {
      for (let j = 0, jj = template.events.length; j < jj; j++) {
        if (template.events[j].eventTemplateId) {
          comboEvent = this.ComboService.findEventTemplate(combo, template.events[j].eventTemplateId);
        } else {
          comboEvent = this.ComboService.findEventTemplateByName(combo, template.events[j].eventName);
        }
        // Update the combo with previously selected combo items
        selectedTemplateIds.push(comboEvent.id);

        // Ensure there isn't a newly selected date
        if (!comboEvent.selectedDate && template.events[j].eventStartTime) {
          selectedStartDate = template.events[j].eventStartTime.split('T');
          comboEvent.selectedDate = selectedStartDate[0] + 'T00:00:00';
        } else if (comboEvent.selectedDate) {
          template.events[j].eventStartTime = comboEvent.selectedDate;
        }
        comboEvent.selectedHourId = template.events[j].eventId;
      }

      if (selectedTemplateIds.length > 0) {
        combo.selectedTemplateIds = selectedTemplateIds.join(',');
      }

      if (combo.type === 'fixedPrice') {
        for (let i = 0, ii = template.tickets.length; i < ii; i++) {
          for (let j = 0, jj = combo.priceList.prices.length; j < jj; j++) {
            if (combo.priceList.prices[j].personType.id === template.tickets[i].ticketTypeId) {
              combo.priceList.prices[j].quantity = template.tickets[i].quantity;
              break;
            }
          }
        }
      } else if (combo.type === 'discountCode') {
        // Reapply values to discount
        if (template.tickets instanceof Array) {
          for (let i = 0, ii = template.tickets.length; i < ii; i++) {
            // Restore to discount object
            for (let j = 0, jj = combo.discount.values.length; j < jj; j++) {
              if (combo.discount.values[j].personType.id === template.tickets[i].ticketTypeId) {
                combo.discount.values[j].quantity = template.tickets[i].quantity;
                break;
              }
            }
          }
        }
      }

      if (template.hasOwnProperty('addOns') && combo.hasOwnProperty('addOns') && combo.addOns instanceof Array) {
        for (let i = 0, ii = template.addOns.length; i < ii; i++) {
          for (let j = 0, jj = combo.addOns.length; j < jj; j++) {
            if (combo.addOns[j].addOnId === template.addOns[i].addOnId) {
              combo.addOns[j].totalQuantity = template.addOns[i].quantity;
              break;
            }
          }
        }
      }
    }

    return combo;
  }

  hasItems() {
    return this.hasEventItems() || this.hasComboItems() || this.hasMembershipItems() || this.hasDonationItems();
  }

  hasNonMembershipItems() {
    return this.hasEventItems() || this.hasComboItems() || this.hasDonationItems();
  }

  hasEventItems() {
    const templates = this.data.templates ?? [];
    return Array.isArray(templates) && templates.length > 0;
  }

  hasForms() {
    return this.data.hasOwnProperty('forms') && this.data.forms instanceof Array && this.data.forms.length;
  }

  hasMembershipItems() {
    return (
      this.data.hasOwnProperty('memberships') &&
      this.data.memberships instanceof Array &&
      this.data.memberships.length > 0
    );
  }

  hasDonationItems() {
    return (
      this.data.hasOwnProperty('donations') && this.data.donations instanceof Array && this.data.donations.length > 0
    );
  }

  hasComboItems() {
    return (
      this.data.hasOwnProperty('comboItems') && this.data.comboItems instanceof Array && this.data.comboItems.length > 0
    );
  }

  findForm(id, itemId, pricePointId) {
    for (let i = 0, ii = this.data.forms.length; i < ii; i++) {
      if (
        this.data.forms[i].formId === id &&
        this.data.forms[i].itemId === itemId &&
        this.data.forms[i].pricePointId === pricePointId
      ) {
        return i;
      }
    }

    return false;
  }

  findStoredForm(props) {
    for (let i = 0, ii = this.store.forms.length; i < ii; i++) {
      if (
        this.store.forms[i].formId === props.formId &&
        this.store.forms[i].itemId === props.itemId &&
        this.store.forms[i].itemType === props.itemType &&
        this.store.forms[i].itemName === props.itemName &&
        this.store.forms[i].pricePointId === props.pricePointId &&
        this.store.forms[i].pricePointName === props.pricePointName &&
        this.store.forms[i].oncePerCart === props.oncePerCart
      ) {
        return {
          index: i,
          form: this.store.forms[i],
        };
      }
    }

    return false;
  }

  findItem(id: TODO, type?: TODO, subType?: TODO, isGift?: boolean) {
    let r;

    if (type === 'combo') {
      r = this.findComboItem(id);
    } else if (this.isMembershipType(type)) {
      r = this.findMembershipItem(id, null, isGift);
    } else if (this.isDonationType({ itemType: type, type: subType })) {
      r = this.findDonationItem(id);
    } else {
      r = this.findEventItem(id);
    }

    return r;
  }

  findEventItem(id) {
    if (this.data && this.data.templates instanceof Array) {
      for (let i = 0, ii = this.data.templates.length; i < ii; i++) {
        if (this.data.templates[i].eventTemplateId === id) {
          return i;
        }
      }
    }

    return false;
  }

  findMembershipItem(id, memberships, gift) {
    gift = gift || false;

    memberships = memberships instanceof Array ? memberships : this.data.memberships;

    for (let i = 0, ii = memberships.length; i < ii; i++) {
      if (
        memberships[i].membershipInfo &&
        memberships[i].membershipInfo.membershipCategoryId === id &&
        memberships[i].membershipInfo.isGift === gift
      ) {
        return i;
      }
    }
    return false;
  }

  findMembershipItemById(id: TODO, memberships?: TODO) {
    memberships = memberships instanceof Array ? memberships : this.data.memberships;

    for (let i = 0, ii = memberships.length; i < ii; i++) {
      if (memberships[i].id === id) {
        return i;
      }
    }

    return false;
  }

  findMembershipItemByItem(membershipCategory, memberships) {
    memberships = memberships instanceof Array ? memberships : this.data.memberships;

    for (let i = 0, ii = memberships.length; i < ii; i++) {
      if (memberships[i].id === membershipCategory.id && memberships[i].action === membershipCategory.action) {
        return i;
      }
    }

    return false;
  }

  findDonationItem(id) {
    for (let i = 0, ii = this.data.donations.length; i < ii; i++) {
      if (this.data.donations[i].inventoryId === id) {
        return i;
      }
    }

    return false;
  }

  findComboItem(id) {
    for (let i = 0, ii = this.data.comboItems.length; i < ii; i++) {
      if (this.data.comboItems[i].comboTemplateId === id) {
        return i;
      }
    }

    return false;
  }

  findComboItemEvent(id, eventTemplateId) {
    let result: TODO | false = false;

    outer: for (let i = 0, ii = this.data.comboItems.length; i < ii; i++) {
      if (this.data.comboItems[i].comboTemplateId === id) {
        for (let j = 0, jj = this.data.comboItems[i].events.length; j < jj; j++) {
          if (this.data.comboItems[i].events[j].eventTemplateId === eventTemplateId) {
            result = {
              comboIndex: i,
              eventTemplateIndex: j,
            };
            break outer;
          }
        }
      }
    }
    return result;
  }

  storeCombos() {
    for (let i = 0, ii = this.data.comboItems.length; i < ii; i++) {
      const combo = this.fetchCombo(this.data.comboItems[i].comboTemplateId);

      if (!combo) {
        this.store.combos[this.data.comboItems[i].comboTemplateId] = {
          events: [],
        };
      }

      for (let j = 0, jj = this.data.comboItems[i].events.length; j < jj; j++) {
        this.store.combos[this.data.comboItems[i].comboTemplateId].events.push(this.data.comboItems[i].events[j]);
      }
    }
  }

  storeForms() {
    this.store.forms = angular.copy(this.data.forms);
  }

  clearStore(type) {
    this.store[type] = {};
  }

  hasStore(type) {
    let r = false;

    if (this.store.hasOwnProperty(type)) {
      if (this.store[type] instanceof Array && this.store[type].length) {
        r = true;
      } else {
        for (const i in this.store[type]) {
          if (this.store[type].hasOwnProperty(i)) {
            r = true;
            break;
          }
        }
      }
    }

    return r;
  }

  fetchCombos() {
    return this.store.combos;
  }

  fetchCombo(id): TODO {
    let comboId;
    let r = false;

    if (this.hasStore('combos')) {
      for (comboId in this.store.combos) {
        if (this.store.combos.hasOwnProperty(comboId) && comboId === id) {
          r = this.store.combos[comboId];
          break;
        }
      }
    }

    return r;
  }

  /**
   * Only allow events with more than one ticket purchased
   *
   */
  clean() {
    this.cleanTemplates();
    this.cleanCombos();
    //TODO: clean memberships?
    this.refreshCartSession();
  }

  cleanTemplates() {
    let quantity;

    for (let i = this.data.templates.length - 1, ii = 0; i >= ii; i--) {
      quantity = 0;
      if (this.data.templates[i]) {
        for (let j = 0, jj = this.data.templates[i].events.length; j < jj; j++) {
          for (let k = 0, kk = this.data.templates[i].events[j].tickets.length; k < kk; k++) {
            quantity += this.data.templates[i].events[j].tickets[k].quantity;
          }
        }
      }
      if (quantity <= 0) {
        this.data.templates.splice(i, 1);
      }
    }
  }

  cleanCombos() {
    let quantity;

    for (let i = this.data.comboItems.length - 1, ii = 0; i >= ii; i--) {
      quantity = 0;
      if (this.data.comboItems[i]) {
        for (let j = 0, jj = this.data.comboItems[i].tickets.length; j < jj; j++) {
          quantity += this.data.comboItems[i].tickets[j].quantity;
        }
      }
      if (quantity <= 0) {
        this.data.comboItems.splice(i, 1);
      }
    }
  }

  clearItems() {
    this.data.templates = [];
    this.data.comboItems = [];
    this.data.memberships = [];
    this.data.donations = [];
    this.data.forms = [];
  }

  removeItem(item) {
    const index = this.findItem(item.id, item.itemType, item.type);

    if (index) {
      this.data.templates.splice(index, 1);
    }

    this.refreshCartSession();
  }

  //sort this so the base membership is always on top
  orderMembershipsByType(items) {
    let memberships: TODO[] = [];
    let gifts;
    let nonGifts;

    if (items instanceof Array) {
      memberships = items.sort(function (a, b) {
        let r = 0;

        if (a && a.membershipInfo && b && b.membershipInfo) {
          if (!a.membershipInfo.isGift && b.membershipInfo.isGift) {
            r = 1;
          } else {
            r = -1;
          }
        }

        return r;
      });

      gifts = memberships.filter(function (membership) {
        return membership.membershipInfo.isGift;
      });
      nonGifts = memberships.filter(function (membership) {
        return !membership.membershipInfo.isGift;
      });

      gifts = gifts.sort(function (a, b) {
        let r = 0;

        if (a && a.membershipInfo && b && b.membershipInfo) {
          if (!a.membershipInfo.auxiliary && b.membershipInfo.auxiliary) {
            r = -1;
          } else {
            r = 1;
          }
        }

        return r;
      });

      nonGifts = nonGifts.sort(function (a, b) {
        let r = 0;

        if (a && a.membershipInfo && b && b.membershipInfo) {
          if (a.membershipInfo.auxiliary < b.membershipInfo.auxiliary) {
            r = -1;
          } else {
            r = 1;
          }
        }

        return r;
      });

      memberships = gifts.concat(nonGifts);
    } else {
      memberships = items;
    }

    return memberships;
  }

  /**
   * Picks a subset of properties for every item in the array
   */
  mapPick(list: TODO[], keys: string[]): TODO[] {
    return _.map(list, (item) => _.pick(item, keys));
  }

  _normalizeTemplatesCartData(cartData: Record<string, any>) {
    const templates = _.get(cartData, 'templates', []);

    function _normalizeTickets(tickets) {
      return _.map(tickets, function (ticket) {
        return _.pick(ticket, ['id', 'quantity', 'ticketTypeId']);
      });
    }

    function _normalizeAddOns(addOns) {
      return _.map(addOns, function (addOn) {
        return _.pick(addOn, ['id', 'quantity', 'ticketTypeId', 'inventoryId']);
      });
    }

    function _normalizeEvents(events) {
      return _.map(events, function (event) {
        const tickets = _normalizeTickets(event.tickets);
        const addOns = _normalizeAddOns(event.addOns);

        return {
          eventId: event.eventId,
          tickets: tickets,
          addOns: addOns,
        };
      });
    }

    return _.map(templates, (template) => {
      const events = _normalizeEvents(template.events);

      return {
        eventTemplateId: template.eventTemplateId,
        events: events,
      };
    });
  }

  _normalizeComboItemsCartData(cartData: Record<string, any>) {
    const comboItems = _.get(cartData, 'comboItems', []);

    function _normalizeEvents(events) {
      return this.mapPick(events, ['eventTemplateId', 'eventId']);
    }

    function _normalizeAddOns(addOns) {
      return this.mapPick(addOns, ['addOnId', 'quantity', 'unitPrice']);
    }

    function _normalizeTickets(tickets) {
      return this.mapPick(tickets, ['eventTemplateId', 'ticketTypeId', 'quantity']);
    }

    return _.map(comboItems, function (comboItem) {
      const events = _normalizeEvents(comboItem.events);
      const addOns = _normalizeAddOns(comboItem.addOns);
      const tickets = _normalizeTickets(comboItem.tickets);

      return {
        comboTemplateId: comboItem.comboTemplateId,
        events: events,
        addOns: addOns,
        tickets: tickets,
      };
    });
  }

  _normalizeMembershipsCartData(cartData: Record<string, any>) {
    const memberships = _.get(cartData, 'memberships', []);

    return _.map(memberships, (membership) => {
      return {
        id: membership.id,
        quantity: membership.quantity,
        membershipInfo: {
          membershipCategoryId: membership.membershipInfo.membershipCategoryId,
          membershipOfferingId: membership.membershipInfo.membershipOfferingId,
          pricePointId: membership.membershipInfo.pricePointId,
        },
      };
    });
  }

  _normalizeDonationsCartData(cartData: Record<string, any>) {
    const donations = _.get(cartData, 'donations', []);

    return this.mapPick(donations, ['id', 'type', 'amount', 'quantity', 'inventoryId']);
  }

  _normalizeFormsCartData(cartData: Record<string, any>) {
    const forms = _.get(cartData, 'forms', []);

    return this.mapPick(forms, ['formId', 'formResponseId', 'itemId']);
  }

  /**
   * Returns a stripped down version of our cart data to make it possible to use deep isEqual.
   * This has to be done because we attach a lot of frontend-only properties to the cart data.
   */
  normalizeCartDataForEqualityCheck(cartData: Record<string, any>) {
    const topLevelProps = _.pick(cartData, [
      'id',
      'visitorId',
      'tempVisitorId',
      'tenantId',
      'membershipId',
      'membershipIds',
      'couponCode',
      'couponCodes',
    ]);

    return _.assign(topLevelProps, {
      templates: this._normalizeTemplatesCartData(cartData),
      comboItems: this._normalizeComboItemsCartData(cartData),
      memberships: this._normalizeMembershipsCartData(cartData),
      donations: this._normalizeDonationsCartData(cartData),
      forms: this._normalizeFormsCartData(cartData),
    });
  }

  /**
   * Extracts eventIds from cartData for comparison
   */
  getTemplateEventIdsFromCartData(cartData: Record<string, any>) {
    return (cartData ? cartData.templates : []).map((template) => {
      return (template.events || []).map((event) => event.eventId);
    });
  }

  /**
   * Retrieves the shopping cart from backend and log an info event to bugsnag if
   * eventIds are different from what we have in web storage
   */
  verifyEventIdsAreConsistentBetweenStorageAndBackend() {
    return this.getShoppingCart().then((cartDataFromBackend) => {
      const eventIdsFromCartServiceData = this.getTemplateEventIdsFromCartData(this.data);
      const eventIdsFromBackendResponse = this.getTemplateEventIdsFromCartData(cartDataFromBackend);
      const eventIdsAreTheSame = _.isEqual(eventIdsFromCartServiceData, eventIdsFromBackendResponse);

      if (!eventIdsAreTheSame) {
        const error = new Error('Discrepancies in event ids were found');

        this.bugsnagService.notify(error, function (event) {
          event.severity = 'info';
          event.context = 'checkout-order-review';
          const cartParams = this.getCartInitParams();
          event.addMetadata('cartParams', cartParams);
          event.addMetadata('eventIds', {
            fromBackend: eventIdsFromBackendResponse,
            fromStorage: eventIdsFromCartServiceData,
          });
        });

        throw error;
      }
    });
  }

  /**
   * Return Cart Memberships filtered by type
   */
  getMemberships(type: string): TODO[] {
    let filterType: string[] = [];

    switch (type) {
      case 'new':
        filterType = this.membershipService.newPurchaseTypes;
        break;

      case 'renew':
        filterType = this.membershipService.renewUpgradePurchaseTypes;
        break;

      case 'rejoin':
        filterType = this.membershipService.rejoinPurchaseTypes;
        break;

      default:
        filterType = this.membershipService.purchaseTypesArray;
        break;
    }

    const memberships = this.hasMembershipItems() ? this.data.memberships : [];

    return memberships.filter((item) => {
      if (filterType.indexOf(item.itemType) > -1) {
        return item;
      }
    });
  }
}

export const CartServiceModule = angular
  .module('acme.common.service.cart', [
    EventsServiceModule,
    ComboServiceModule,
    FormsServiceModule,
    ApiVersionFactoryModule,
    QueryStringBuilderFactoryModule,
    MembershipServiceModule,
    LocalStorageServiceModule,
    BugsnagServiceModule,
  ])
  .service('CartService', CartService).name;

/**
 * Helper to make sure event listeners are removed with the correct arguments
 * @returns Listener clean up function
 */
function onEvent(element: HTMLElement, eventName: string, listener: (event: Event) => void) {
  element.addEventListener(eventName, listener);

  return function () {
    element.removeEventListener(eventName, listener);
  };
}
